import React, { useEffect, useMemo, useState } from 'react';
import { useNotification } from '../../../../components/framework/NotificationContext';
import { useConfirmation } from '../../../../contexts/ConfirmationContext';
import {
    getCampaigns,
    deleteCampaign,
    resendCampaign,
    duplicateCampaign,
    cancelCampaign,
    exportCampaignsCsv,
} from '../../../../services/emailCampaignService';

const PAGE_SIZE = 20;

const STATUS_LABELS = {
    drafts: 'DRAFT',
    scheduled: 'SCHEDULED',
    sent: 'SENT',
    failed: 'FAILED',
    history: null, // no filter — all statuses
};

const TITLES = {
    drafts: 'Drafts',
    scheduled: 'Scheduled Emails',
    sent: 'Sent Emails',
    failed: 'Failed Deliveries',
    history: 'Email History',
};

/**
 * Reusable, paginated campaign table — drives Drafts/Scheduled/Sent/Failed/History
 * nav sections via the `section` prop, matching DatasetManagement's table conventions.
 */
const HistoryPanel = ({ section }) => {
    const { success, error: notifyError } = useNotification();
    const confirm = useConfirmation();
    const [campaigns, setCampaigns] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [detail, setDetail] = useState(null);

    const statusFilter = STATUS_LABELS[section];

    const load = () => {
        setLoading(true);
        const params = { page, size: PAGE_SIZE };
        if (statusFilter) params.status = statusFilter;
        if (search) params.subject = search;
        getCampaigns(params)
            .then((data) => {
                setCampaigns(data?.content || data || []);
                setTotal(data?.totalElements ?? (data?.content || data || []).length);
            })
            .catch((err) => notifyError(err.message || 'Failed to load email history'))
            .finally(() => setLoading(false));
    };

    useEffect(load, [section, page]);

    const debouncedSearch = useMemo(() => {
        let t;
        return (value) => {
            clearTimeout(t);
            t = setTimeout(() => { setPage(0); setSearch(value); }, 400);
        };
    }, []);

    const handleResend = async (c) => {
        const ok = await confirm({ title: 'Resend email?', message: `Resend "${c.subjectTemplate || c.subject}" to its recipients?`, confirmLabel: 'Resend' });
        if (!ok) return;
        try {
            await resendCampaign(c.id);
            success('Resend queued');
            load();
        } catch (err) {
            notifyError(err.message || 'Failed to resend');
        }
    };

    const handleDuplicate = async (c) => {
        try {
            await duplicateCampaign(c.id);
            success('Campaign duplicated as a new draft');
            load();
        } catch (err) {
            notifyError(err.message || 'Failed to duplicate');
        }
    };

    const handleCancel = async (c) => {
        const ok = await confirm({ title: 'Cancel scheduled email?', message: 'This will cancel delivery to any recipients not yet sent to.', confirmLabel: 'Cancel Send', type: 'danger' });
        if (!ok) return;
        try {
            await cancelCampaign(c.id);
            success('Campaign cancelled');
            load();
        } catch (err) {
            notifyError(err.message || 'Failed to cancel');
        }
    };

    const handleDelete = async (c) => {
        const ok = await confirm({ title: 'Delete from history?', message: 'This permanently removes the record. Continue?', confirmLabel: 'Delete', type: 'danger' });
        if (!ok) return;
        try {
            await deleteCampaign(c.id);
            success('Deleted');
            load();
        } catch (err) {
            notifyError(err.message || 'Failed to delete');
        }
    };

    const handleExport = async () => {
        try {
            const params = statusFilter ? { status: statusFilter } : {};
            const data = await exportCampaignsCsv(params);
            const blob = new Blob([typeof data === 'string' ? data : JSON.stringify(data)], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `email-history-${section}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            notifyError(err.message || 'Failed to export history');
        }
    };

    const statusBadge = (status) => {
        const map = {
            SENT: 'bg-success-subtle text-success',
            FAILED: 'bg-danger-subtle text-danger',
            PARTIALLY_FAILED: 'bg-warning-subtle text-warning',
            SCHEDULED: 'bg-info-subtle text-info',
            SENDING: 'bg-primary-subtle text-primary',
            DRAFT: 'bg-secondary-subtle text-secondary',
            CANCELLED: 'bg-secondary-subtle text-secondary',
        };
        return <span className={`badge ${map[status] || 'bg-light text-dark'}`}>{status}</span>;
    };

    return (
        <div className="admin-card">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <h5 className="mb-0">{TITLES[section]}</h5>
                <div className="d-flex gap-2">
                    <input className="form-control form-control-sm" placeholder="Search…" onChange={(e) => debouncedSearch(e.target.value)} />
                    <button className="btn btn-outline-secondary btn-sm" onClick={load}><i className="bi bi-arrow-clockwise"></i></button>
                    <button className="btn btn-outline-secondary btn-sm" onClick={handleExport}><i className="bi bi-download me-1"></i>Export</button>
                </div>
            </div>

            <div className="mb-2 text-muted small">
                {loading ? 'Loading…' : `Showing ${campaigns.length} of ${total} email${total === 1 ? '' : 's'}`}
            </div>

            {loading ? (
                <div className="text-center py-4"><div className="spinner-border text-primary" role="status"></div></div>
            ) : (
                <div className="table-responsive">
                    <table className="admin-table">
                        <thead>
                            <tr><th>Subject</th><th>Status</th><th>Recipients</th><th>Sent/Scheduled</th><th style={{ textAlign: 'right' }}>Actions</th></tr>
                        </thead>
                        <tbody>
                            {campaigns.length === 0 ? (
                                <tr><td colSpan="5" className="text-center text-muted py-4">No emails found.</td></tr>
                            ) : campaigns.map((c) => (
                                <tr key={c.id}>
                                    <td>{c.subjectTemplate || c.subject}</td>
                                    <td>{statusBadge(c.status)}</td>
                                    <td>{c.sentCount ?? 0}/{c.totalRecipients ?? 0}{c.failedCount ? ` (${c.failedCount} failed)` : ''}</td>
                                    <td className="mono-font" style={{ fontSize: '0.85rem' }}>
                                        {(c.sentAt || c.scheduledAt) ? new Date(c.sentAt || c.scheduledAt).toLocaleString() : '—'}
                                    </td>
                                    <td>
                                        <div className="d-flex gap-1 justify-content-end flex-wrap">
                                            <button className="btn btn-sm btn-outline-secondary" onClick={() => setDetail(c)} title="View"><i className="bi bi-eye"></i></button>
                                            {c.status === 'SCHEDULED' && <button className="btn btn-sm btn-outline-danger" onClick={() => handleCancel(c)} title="Cancel">Cancel</button>}
                                            {(c.status === 'SENT' || c.status === 'FAILED' || c.status === 'PARTIALLY_FAILED') && (
                                                <button className="btn btn-sm btn-outline-primary" onClick={() => handleResend(c)} title="Resend"><i className="bi bi-arrow-repeat"></i></button>
                                            )}
                                            <button className="btn btn-sm btn-outline-secondary" onClick={() => handleDuplicate(c)} title="Duplicate"><i className="bi bi-files"></i></button>
                                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(c)} title="Delete"><i className="bi bi-trash"></i></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="d-flex justify-content-between align-items-center mt-3">
                <button className="btn btn-sm btn-outline-secondary" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</button>
                <span className="small text-muted">Page {page + 1}</span>
                <button className="btn btn-sm btn-outline-secondary" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>

            {detail && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: '#fff', padding: '24px', borderRadius: '12px', width: '640px', maxWidth: '92vw', maxHeight: '85vh', overflowY: 'auto' }}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 style={{ margin: 0 }}>{detail.subjectTemplate || detail.subject}</h4>
                            <button className="btn-close" onClick={() => setDetail(null)}></button>
                        </div>
                        <p><strong>Status:</strong> {statusBadge(detail.status)}</p>
                        <p><strong>Recipients:</strong> {detail.totalRecipients} (sent {detail.sentCount}, failed {detail.failedCount})</p>
                        {detail.lastError && <p className="text-danger"><strong>Last error:</strong> {detail.lastError}</p>}
                        <pre className="bg-light p-3 rounded" style={{ maxHeight: '300px', overflow: 'auto', fontSize: '12px' }}>
                            {detail.bodyHtmlTemplate || detail.resolvedBodyHtml || ''}
                        </pre>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HistoryPanel;
